import type { LucideIcon } from 'lucide-react';
import {
  LayoutDashboard, GraduationCap, Clock, ChartLine, FileText, Sparkles, FileCheck, CirclePlus,
  SquareCheck, Bell, Briefcase, Mail, School, BookOpen, Users, SlidersHorizontal, LayoutGrid, Video, ContactRound,
  ClipboardList, Library, ShieldAlert, NotebookPen, MessageSquareText,
} from 'lucide-react';

export type NavLinkItem = { to: string; label: string; icon: LucideIcon; badge?: { value: number; tone: 'alert' | 'neutral' } };
export type NavSection = { heading: string; links: NavLinkItem[] };

const count = (value: number | undefined, tone: 'alert' | 'neutral') =>
  value && value > 0 ? { value, tone } : undefined;

export function sectionsForRole(role: string | null, counts: { alerts: number; requests?: number }): NavSection[] {
  const alerts = count(counts.alerts, 'alert');
  if (role === 'superuser') {
    return [{ heading: 'Platform admin', links: [
      { to: '/admin', label: 'Admin dashboard', icon: LayoutDashboard },
      { to: '/admin/users', label: 'Users', icon: Users },
      { to: '/admin/schools', label: 'Schools', icon: School },
      { to: '/admin/exams', label: 'Exams', icon: FileText },
      { to: '/admin/students', label: 'All students', icon: GraduationCap },
      { to: '/admin/account-sharing', label: 'Account sharing', icon: ShieldAlert },
      { to: '/admin/rag', label: 'RAG knowledge', icon: BookOpen },
      { to: '/admin/chapter-videos', label: 'Chapter videos', icon: Video },
      { to: '/admin/notification', label: 'Push notifications', icon: Bell },
      { to: '/admin/school-ai-usage', label: 'AI usage', icon: ChartLine },
    ]}];
  }
  if (role === 'school_admin' || role === 'school' || role === 'admin') {
    return [
      { heading: 'Overview', links: [
        { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
        { to: '/students', label: 'Students', icon: GraduationCap },
        { to: '/students/requests', label: 'Student requests', icon: Clock, badge: count(counts.requests, 'neutral') },
      ]},
      { heading: 'Academics', links: [
        { to: '/class-performance', label: 'Class performance', icon: ChartLine },
        { to: '/exams/exam-list', label: 'Exam list', icon: FileText },
        { to: '/worksheets', label: 'Worksheets', icon: ClipboardList },
        { to: '/homework', label: 'Homework', icon: NotebookPen },
        { to: '/exam-bank', label: 'Exam Bank', icon: Library },
        { to: '/exams/generate', label: 'Generate exam', icon: Sparkles },
        { to: '/exams/paper', label: 'Paper exams', icon: FileCheck },
        { to: '/exams/add-exam', label: 'Add exam', icon: CirclePlus },
      ]},
      { heading: 'Workflow', links: [
        { to: '/school-tasks', label: 'School tasks', icon: SquareCheck },
        { to: '/alerts', label: 'Alerts', icon: Bell, badge: alerts },
        { to: '/cases', label: 'Cases', icon: Briefcase },
        { to: '/parents/digest', label: 'Parent digest', icon: Mail },
      ]},
      { heading: 'Setup', links: [
        { to: '/school-setup/class', label: 'Classes', icon: School },
        { to: '/school-setup/chapters', label: 'Chapters', icon: BookOpen },
        { to: '/school-setup/staff', label: 'Staff', icon: ContactRound },
        { to: '/school-setup/teachers', label: 'Teachers', icon: Users },
      ]},
      { heading: 'Settings', links: [
        { to: '/settings/alerts', label: 'Alert rules', icon: SlidersHorizontal },
        { to: '/settings/sections', label: 'Sections', icon: LayoutGrid },
        { to: '/settings/coach-activity', label: 'Coach activity', icon: MessageSquareText },
        { to: '/settings/ai', label: 'My AI', icon: Sparkles },
      ]},
    ];
  }
  if (role === 'teacher') {
    return [{ heading: 'My panel', links: [
      { to: '/dashboard', label: 'My dashboard', icon: LayoutDashboard },
      { to: '/students', label: 'My students', icon: GraduationCap },
      { to: '/class-performance', label: 'Class performance', icon: ChartLine },
      { to: '/school-tasks', label: 'School tasks', icon: SquareCheck },
      { to: '/alerts', label: 'Alerts', icon: Bell, badge: alerts },
      { to: '/cases', label: 'Cases', icon: Briefcase },
    ]},
    { heading: 'Teaching', links: [
      { to: '/exams/exam-list', label: 'Exam list', icon: FileText },
      { to: '/worksheets', label: 'Worksheets', icon: ClipboardList },
      { to: '/homework', label: 'Homework', icon: NotebookPen },
      { to: '/exam-bank', label: 'Exam Bank', icon: Library },
    ]},
    { heading: 'Account', links: [{ to: '/settings/ai', label: 'My AI', icon: Sparkles }] }];
  }
  if (role === 'counselor') {
    return [{ heading: 'Counselor', links: [
      { to: '/dashboard', label: 'Counselor dashboard', icon: LayoutDashboard },
      { to: '/students', label: 'Students', icon: GraduationCap },
      { to: '/alerts', label: 'Wellbeing alerts', icon: Bell, badge: alerts },
      { to: '/cases', label: 'Wellbeing cases', icon: Briefcase },
      { to: '/class-performance', label: 'Class performance', icon: ChartLine },
    ]},
    { heading: 'Account', links: [{ to: '/settings/ai', label: 'My AI', icon: Sparkles }] }];
  }
  return [];
}
